import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import TakeoutDiningRoundedIcon from "@mui/icons-material/TakeoutDiningRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Images from "../utils/Images";
import { toMoneyNumber } from "../utils/tax";
import {
    fetchQuickOrderByRazorpayId,
    getLastRazorpayOrderId,
    getOrderNumber,
    getOrderStatusKey,
    getTrackedOrders,
    isCompletedStatus,
    isCreatedCustomerOrder,
    removeTrackedOrder,
    updateTrackedOrder,
} from "../utils/lastOrder";
import { getPendingPayment } from "../utils/pendingPayment";

const ORDER_STATUS_POLL_MS = 120 * 1000;
const COUNTDOWN_TICK_MS = 15 * 1000;

const STATUS_STEPS = [
    {
        key: "PLACED",
        label: "Order placed",
        hint: "Sent to the cafe",
    },
    {
        key: "PREPARING",
        label: "Preparing",
        hint: "The kitchen is preparing it",
    },
    {
        key: "READY",
        label: "Ready",
        hint: "Collect it at the counter",
    },
];

const READY_STATUSES = new Set([
    "COMPLETED",
    "DELIVERED",
    "PICKED_UP",
    "DONE",
    "CLOSED",
]);

const CANCELLED_STATUSES = new Set([
    "CANCELLED",
    "CANCELED",
    "FAILED",
    "REJECTED",
]);

const formatMoney = (value) => `₹${toMoneyNumber(value).toFixed(2)}`;

const formatPlacedAt = (value) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const parseMakingMinutes = (makingTime) => {
    if (makingTime == null || makingTime === "") return null;

    if (typeof makingTime === "number" && Number.isFinite(makingTime)) {
        return makingTime;
    }

    const match = String(makingTime).match(/(\d+)/);
    return match ? Number(match[1]) : null;
};

const getPlacedAtDate = (order) => {
    const raw =
        order?.createdAt ||
        order?.created_at ||
        order?.paymentInitiatedAt ||
        order?.customerCartData?.paymentInitiatedAt;

    if (!raw) return null;

    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
};

const parseReadyClock = (readyTime, placedAt) => {
    if (!readyTime) return null;

    const asDate = new Date(readyTime);
    if (
        typeof readyTime === "string" &&
        readyTime.length > 8 &&
        !Number.isNaN(asDate.getTime())
    ) {
        return asDate;
    }

    const match = String(readyTime)
        .trim()
        .match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);

    if (!match) return null;

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = match[3];

    if (meridiem) {
        hours = (hours % 12) + (meridiem.toUpperCase() === "PM" ? 12 : 0);
    }

    const base = placedAt ? new Date(placedAt) : new Date();
    const readyAt = new Date(base);
    readyAt.setHours(hours, minutes, 0, 0);

    if (placedAt && readyAt.getTime() < placedAt.getTime() - 5 * 60 * 1000) {
        readyAt.setDate(readyAt.getDate() + 1);
    }

    return readyAt;
};

const getEstimatedReadyAt = (order, makingTime, readyTime) => {
    const placedAt = getPlacedAtDate(order);
    const fromClock = parseReadyClock(readyTime, placedAt);

    if (fromClock) return fromClock;

    const minutes = parseMakingMinutes(makingTime);
    if (!placedAt || minutes == null) return null;

    return new Date(placedAt.getTime() + minutes * 60 * 1000);
};

const getPreparingMessage = ({ isOverdue, makingTime, readyTime }) => {
    if (isOverdue) {
        return "It's taking a little longer than expected. Hang tight — the cafe is still preparing your order.";
    }

    const parts = ["The cafe is preparing your order."];

    if (makingTime) parts.push(`About ${makingTime}.`);
    if (readyTime) parts.push(`Ready around ${readyTime}.`);

    return parts.join(" ");
};

const getAddonLabel = (addon) => {
    const valueName =
        addon?.selectedValue ||
        addon?.label ||
        addon?.name ||
        addon?.addonName ||
        addon?.productName;

    const addonTitle = addon?.addonTitle;

    if (valueName && addonTitle && addonTitle !== valueName) {
        return `${addonTitle}: ${valueName}`;
    }

    return valueName || addonTitle || "";
};

const getStatusMeta = (statusKey) => {
    if (CANCELLED_STATUSES.has(statusKey)) {
        return {
            label: "Cancelled",
            color: "#D94848",
            background: "#FDECEC",
            stepIndex: -1,
            cancelled: true,
            isPreparing: false,
            isReady: false,
        };
    }

    if (READY_STATUSES.has(statusKey)) {
        return {
            label: "Ready",
            color: "#25BA6C",
            background: "#E8F8EF",
            stepIndex: 2,
            cancelled: false,
            isPreparing: false,
            isReady: true,
        };
    }

    return {
        label: "Preparing",
        color: "#F08A24",
        background: "#FEF4E8",
        stepIndex: 1,
        cancelled: false,
        isPreparing: true,
        isReady: false,
    };
};

const getPickupLabel = (order) => {
    const pickup = order?.pickupOption || order?.ordersType || order?.orderType;

    if (pickup === "TAKE-AWAY") return "Takeaway";
    if (pickup === "DINE_IN" || pickup === "DINE-IN") return "Dine in";
    if (pickup === "PICKUP") return "Pickup";

    return "Pickup";
};

const getOrderItems = (order) => {
    const apiItems =
        order?.orderItems ||
        order?.items ||
        order?.products ||
        order?.cartItems ||
        order?.customerCartItemsData ||
        [];

    return (Array.isArray(apiItems) ? apiItems : []).map((item, index) => {
        const product =
            item?.product && typeof item.product === "object"
                ? item.product
                : {};

        return {
            id: item?._id || product?._id || item?.product || index,
            name: product?.name || item?.productName || item?.name || "Item",
            image:
                product?.displayImage ||
                product?.displayImageUrl ||
                product?.coverPicture ||
                item?.productImage ||
                Images.product,
            quantity: Number(item?.quantity || 1),
            addons: Array.isArray(item?.addons) ? item.addons : [],
            total:
                item?.taxable ??
                item?.totalTaxable ??
                (item?.grandTotal != null && item?.taxAmount != null
                    ? toMoneyNumber(item.grandTotal) -
                      toMoneyNumber(item.taxAmount)
                    : item?.totalPrice ?? item?.price ?? 0),
        };
    });
};

const OrderStatus = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { outletId: outletIdFromPath } = useParams();

    const outletId =
        outletIdFromPath || localStorage.getItem("outletId");
    const razorpayOrderId =
        location.state?.razorpayOrderId || getLastRazorpayOrderId(outletId);

    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(Boolean(razorpayOrderId));
    const [error, setError] = useState("");
    const [nowMs, setNowMs] = useState(() => Date.now());

    const loadOrder = useCallback(async ({ silent = false } = {}) => {
        if (!razorpayOrderId) {
            setOrder(null);
            setIsLoading(false);
            setError("");
            return;
        }

        if (!silent) {
            setIsLoading(true);
            setError("");
        }

        let loaded = false;

        try {
            const fetchedOrder = await fetchQuickOrderByRazorpayId(
                razorpayOrderId,
            );

            if (!fetchedOrder || !isCreatedCustomerOrder(fetchedOrder)) {
                throw new Error("Order details were not found.");
            }

            loaded = true;
            setOrder(fetchedOrder);
            updateTrackedOrder(outletId, razorpayOrderId, {
                orderNumber: getOrderNumber(fetchedOrder),
                status: getOrderStatusKey(fetchedOrder),
            });
        } catch (apiError) {
            console.error(
                "get-razorpay-order-quick-order:",
                apiError?.response?.data || apiError,
            );

            const pending = getPendingPayment();
            const isPendingThisOrder =
                pending?.razorpayOrderId === razorpayOrderId;

            if (apiError?.response?.status === 404 && !isPendingThisOrder) {
                removeTrackedOrder(outletId, razorpayOrderId);
            }

            if (!silent && !isPendingThisOrder) {
                setOrder(null);
                setError(
                    apiError?.response?.data?.message ||
                        apiError?.response?.data?.error ||
                        apiError?.message ||
                        "Unable to load this order. Please try again.",
                );
            }
        } finally {
            if (!silent) {
                const pending = getPendingPayment();
                if (
                    loaded ||
                    pending?.razorpayOrderId !== razorpayOrderId
                ) {
                    setIsLoading(false);
                }
            }
        }
    }, [outletId, razorpayOrderId]);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    useEffect(() => {
        if (order || !razorpayOrderId) return undefined;

        const pending = getPendingPayment();
        if (pending?.razorpayOrderId !== razorpayOrderId) return undefined;

        const pollId = window.setInterval(() => {
            loadOrder({ silent: true });
        }, 2000);

        const stopId = window.setTimeout(() => {
            window.clearInterval(pollId);
            setIsLoading(false);
        }, 45000);

        return () => {
            window.clearInterval(pollId);
            window.clearTimeout(stopId);
        };
    }, [loadOrder, order, razorpayOrderId]);

    const isPreparing =
        Boolean(order) && getStatusMeta(getOrderStatusKey(order)).isPreparing;
    const readyTimeValue = order?.readyTime || order?.ready_time;
    const makingTimeValue =
        order?.makingTimeDuration ||
        order?.totalMakingTime ||
        order?.makingTime;
    const placedAtRaw =
        order?.createdAt ||
        order?.created_at ||
        order?.paymentInitiatedAt ||
        order?.customerCartData?.paymentInitiatedAt;

    useEffect(() => {
        if (!isPreparing) return undefined;

        const estimatedReadyAt = getEstimatedReadyAt(
            { createdAt: placedAtRaw },
            makingTimeValue,
            readyTimeValue,
        );
        const msUntilOverdue = estimatedReadyAt
            ? estimatedReadyAt.getTime() - Date.now()
            : null;

        const tickId = window.setInterval(() => {
            setNowMs(Date.now());
        }, COUNTDOWN_TICK_MS);

        const overdueId =
            msUntilOverdue != null && msUntilOverdue > 0
                ? window.setTimeout(() => {
                      setNowMs(Date.now());
                  }, msUntilOverdue)
                : null;

        const pollId = window.setInterval(() => {
            loadOrder({ silent: true });
        }, ORDER_STATUS_POLL_MS);

        return () => {
            window.clearInterval(tickId);
            window.clearInterval(pollId);
            if (overdueId) window.clearTimeout(overdueId);
        };
    }, [
        isPreparing,
        loadOrder,
        makingTimeValue,
        placedAtRaw,
        readyTimeValue,
    ]);

    const orderNumber = getOrderNumber(order);
    const statusKey = getOrderStatusKey(order);
    const statusMeta = getStatusMeta(statusKey);
    const items = getOrderItems(order);
    const totalItemQuantity = items.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0,
    );
    const pickupLabel = getPickupLabel(order);
    const isTakeaway = pickupLabel === "Takeaway";
    const placedAt =
        formatPlacedAt(order?.createdAt) ||
        formatPlacedAt(order?.created_at) ||
        formatPlacedAt(order?.paymentInitiatedAt);
    const readyTime = readyTimeValue;
    const makingTime = makingTimeValue;
    const estimatedReadyAt = getEstimatedReadyAt(order, makingTime, readyTime);
    const isEstimateOverdue =
        statusMeta.isPreparing &&
        Boolean(estimatedReadyAt) &&
        nowMs >= estimatedReadyAt.getTime();
    const paymentMode = order?.paymentMode || "ONLINE";
    const paymentStatus = String(
        order?.paymentStatus || (paymentMode === "ONLINE" ? "PAID" : "PENDING"),
    ).toUpperCase();
    const cartData = order?.customerCartData || {};

    const taxable =
        order?.totalTaxable ??
        order?.taxable ??
        cartData?.totalTaxable ??
        0;
    const taxAmount =
        order?.totalTaxAmount ??
        order?.taxAmount ??
        cartData?.totalTaxAmount ??
        0;
    const grandTotal =
        order?.grandTotal ??
        order?.totalAmount ??
        cartData?.grandTotal ??
        0;

    const goToMenu = () => {
        navigate(outletId ? `/${outletId}` : "/", { replace: true });
    };

    const goBack = () => {
        const remaining = getTrackedOrders(outletId);

        if (remaining.length > 1) {
            navigate(`/${outletId}/orders`, { replace: true });
            return;
        }

        goToMenu();
    };

    const handleDismissOrder = () => {
        removeTrackedOrder(outletId, razorpayOrderId);
        const remaining = getTrackedOrders(outletId);

        if (remaining.length > 0) {
            navigate(`/${outletId}/orders`, { replace: true });
            return;
        }

        goToMenu();
    };

    if (!razorpayOrderId) {
        return (
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                    background:
                        "linear-gradient(180deg, #DDEFF6 0px, #F4FBFE 120px, #F8FDFF 100%)",
                    textAlign: "center",
                }}
            >
                <ReceiptLongRoundedIcon
                    sx={{ fontSize: 52, color: "#9EB8C4", mb: 1.5 }}
                />
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#17364a" }}>
                    No recent order
                </Typography>
                <Typography sx={{ mt: 1, color: "#577383", fontSize: 15 }}>
                    Place an order to track it here.
                </Typography>
                <Button
                    variant="contained"
                    onClick={goToMenu}
                    sx={{
                        mt: 3,
                        minHeight: 48,
                        px: 3,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontWeight: 800,
                        backgroundColor: "#003049",
                    }}
                >
                    Back to menu
                </Button>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    background:
                        "linear-gradient(180deg, #DDEFF6 0px, #F4FBFE 120px, #F8FDFF 100%)",
                }}
            >
                <CircularProgress sx={{ color: "#09A5F7" }} />
                <Typography sx={{ color: "#3E6A7C", fontWeight: 700 }}>
                    Loading order details...
                </Typography>
            </Box>
        );
    }

    if (error || !order) {
        return (
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                    background:
                        "linear-gradient(180deg, #DDEFF6 0px, #F4FBFE 120px, #F8FDFF 100%)",
                    textAlign: "center",
                }}
            >
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#17364a" }}>
                    Couldn&apos;t load order
                </Typography>
                <Typography sx={{ mt: 1, color: "#577383", fontSize: 15 }}>
                    {error || "Please try again."}
                </Typography>
                <Button
                    variant="contained"
                    onClick={loadOrder}
                    sx={{
                        mt: 3,
                        minHeight: 48,
                        px: 3,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontWeight: 800,
                        backgroundColor: "#003049",
                    }}
                >
                    Try again
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                background:
                    "linear-gradient(180deg, #DDEFF6 0px, #F4FBFE 140px, #F8FDFF 100%)",
                pb: "max(24px, env(safe-area-inset-bottom))",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 56,
                    px: 1.25,
                    py: 1,
                    backgroundColor: "#09A5F7",
                    borderBottomLeftRadius: "18px",
                    borderBottomRightRadius: "18px",
                }}
            >
                <IconButton
                    onClick={goBack}
                    aria-label="Back"
                    sx={{ color: "#FFFFFF" }}
                >
                    <ArrowBackRoundedIcon />
                </IconButton>
                <Typography
                    sx={{
                        color: "#FFFFFF",
                        fontSize: 18,
                        fontWeight: 800,
                        flex: 1,
                    }}
                >
                    Order status
                </Typography>
                <IconButton
                    onClick={loadOrder}
                    aria-label="Refresh order"
                    sx={{ color: "#FFFFFF" }}
                >
                    <RefreshRoundedIcon />
                </IconButton>
            </Box>

            <Box sx={{ px: 2, pt: 2, maxWidth: 430, mx: "auto" }}>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box
                        sx={{
                            p: 2.2,
                            borderRadius: "22px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #D5EAF3",
                            boxShadow: "0 10px 30px rgba(23, 101, 135, 0.08)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1.5,
                            }}
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        color: "#668594",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        letterSpacing: "0.4px",
                                    }}
                                >
                                    ORDER NUMBER
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.4,
                                        color: "#0B4A67",
                                        fontSize: 26,
                                        fontWeight: 800,
                                        letterSpacing: "-0.6px",
                                    }}
                                >
                                    {orderNumber ? `#${orderNumber}` : "Placed"}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    px: 1.2,
                                    py: 0.6,
                                    borderRadius: "999px",
                                    backgroundColor: statusMeta.background,
                                    color: statusMeta.color,
                                    fontSize: 12,
                                    fontWeight: 800,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {statusMeta.label}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                mt: 1.8,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                            }}
                        >
                            <InfoChip
                                icon={
                                    isTakeaway ? (
                                        <TakeoutDiningRoundedIcon sx={{ fontSize: 16 }} />
                                    ) : (
                                        <RestaurantRoundedIcon sx={{ fontSize: 16 }} />
                                    )
                                }
                                label={pickupLabel}
                            />
                            {placedAt && (
                                <InfoChip
                                    icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />}
                                    label={placedAt}
                                />
                            )}
                            <InfoChip
                                icon={<PaymentsRoundedIcon sx={{ fontSize: 16 }} />}
                                label={`${paymentMode === "ONLINE" ? "Paid online" : paymentMode} · ${paymentStatus}`}
                            />
                        </Box>

                        <Typography
                            sx={{
                                mt: 1.6,
                                color: statusMeta.isReady
                                    ? "#1D9E59"
                                    : isEstimateOverdue
                                      ? "#C56A12"
                                      : statusMeta.isPreparing
                                        ? "#C56A12"
                                        : "#3E6A7C",
                                fontSize: 13,
                                fontWeight: 700,
                            }}
                        >
                            {statusMeta.cancelled
                                ? "This order was cancelled."
                                : statusMeta.isReady
                                  ? "Your order is ready. Please collect it at the counter."
                                  : statusMeta.isPreparing
                                    ? getPreparingMessage({
                                          isOverdue: isEstimateOverdue,
                                          makingTime,
                                          readyTime,
                                      })
                                    : "Your order has been sent to the cafe."}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            mt: 1.6,
                            p: 2.2,
                            borderRadius: "22px",
                            backgroundColor: statusMeta.isReady
                                ? "#F3FBF6"
                                : statusMeta.isPreparing
                                  ? "#FFF9F2"
                                  : "#FFFFFF",
                            border: statusMeta.isReady
                                ? "1px solid #B7E7C9"
                                : statusMeta.isPreparing
                                  ? "1px solid #F6D7B5"
                                  : "1px solid #D5EAF3",
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 1.6,
                                color: "#0B4A67",
                                fontSize: 16,
                                fontWeight: 800,
                            }}
                        >
                            Tracking
                        </Typography>

                        {statusMeta.cancelled ? (
                            <Typography sx={{ color: "#D94848", fontWeight: 700 }}>
                                This order was cancelled. Please speak with the cafe.
                            </Typography>
                        ) : (
                            STATUS_STEPS.map((step, index) => {
                                const done = index <= statusMeta.stepIndex;
                                const current = index === statusMeta.stepIndex;

                                return (
                                    <Box
                                        key={step.key}
                                        sx={{
                                            display: "flex",
                                            gap: 1.4,
                                            pb: index === STATUS_STEPS.length - 1 ? 0 : 1.6,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 18,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: "50%",
                                                    backgroundColor: done
                                                        ? current
                                                            ? statusMeta.color
                                                            : "#25BA6C"
                                                        : "#D5EAF3",
                                                    boxShadow: current
                                                        ? `0 0 0 4px ${statusMeta.background}`
                                                        : "none",
                                                    animation:
                                                        current &&
                                                        statusMeta.isPreparing
                                                            ? "statusPulse 1.6s ease-out infinite"
                                                            : "none",
                                                    "@keyframes statusPulse": {
                                                        "0%": {
                                                            boxShadow:
                                                                "0 0 0 4px #FEF4E8",
                                                        },
                                                        "70%": {
                                                            boxShadow:
                                                                "0 0 0 10px rgba(240, 138, 36, 0)",
                                                        },
                                                        "100%": {
                                                            boxShadow:
                                                                "0 0 0 4px rgba(240, 138, 36, 0)",
                                                        },
                                                    },
                                                }}
                                            />
                                            {index < STATUS_STEPS.length - 1 && (
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        width: 2,
                                                        mt: 0.6,
                                                        backgroundColor:
                                                            index < statusMeta.stepIndex
                                                                ? "#25BA6C"
                                                                : "#D5EAF3",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ pb: 0.4 }}>
                                            <Typography
                                                sx={{
                                                    color: done ? "#0B4A67" : "#8BA3AE",
                                                    fontSize: 14,
                                                    fontWeight: current ? 800 : 700,
                                                }}
                                            >
                                                {step.label}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: "#8BA3AE",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {step.key === "PREPARING" &&
                                                isEstimateOverdue
                                                    ? "Taking a little longer than usual"
                                                    : step.hint}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {items.length > 0 && (
                        <Box
                            sx={{
                                mt: 1.6,
                                p: 2.2,
                                borderRadius: "22px",
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #D5EAF3",
                            }}
                        >
                            <Typography
                                sx={{
                                    mb: 1.4,
                                    color: "#0B4A67",
                                    fontSize: 16,
                                    fontWeight: 800,
                                }}
                            >
                                Items
                            </Typography>

                            {items.map((item) => {
                                const addonText = item.addons
                                    .map(getAddonLabel)
                                    .filter(Boolean)
                                    .join(", ");

                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: "flex",
                                            gap: 1.2,
                                            py: 1.1,
                                            borderBottom: "1px solid #E7F2F7",
                                            "&:last-of-type": {
                                                borderBottom: 0,
                                                pb: 0,
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={item.image}
                                            alt={item.name}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: "12px",
                                                objectFit: "cover",
                                                backgroundColor: "#F3F8FB",
                                            }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    color: "#17364a",
                                                    fontSize: 14,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                            {addonText ? (
                                                <Typography
                                                    sx={{
                                                        mt: 0.2,
                                                        color: "#7A93A0",
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {addonText}
                                                </Typography>
                                            ) : null}
                                            <Typography
                                                sx={{
                                                    mt: 0.25,
                                                    color: "#668594",
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Qty {item.quantity}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                color: "#0B4A67",
                                                fontSize: 14,
                                                fontWeight: 800,
                                            }}
                                        >
                                            {formatMoney(item.total)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    <Box
                        sx={{
                            mt: 1.6,
                            p: 2.2,
                            borderRadius: "22px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #D5EAF3",
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 1.2,
                                color: "#0B4A67",
                                fontSize: 16,
                                fontWeight: 800,
                            }}
                        >
                            Bill
                        </Typography>
                        <BillRow
                            label="Items"
                            value={`${totalItemQuantity} ${
                                totalItemQuantity === 1 ? "item" : "items"
                            }`}
                        />
                        <BillRow label="Subtotal" value={formatMoney(taxable)} />
                        <BillRow label="Tax" value={formatMoney(taxAmount)} />
                        <BillRow
                            label="Total paid"
                            value={formatMoney(grandTotal)}
                            emphasize
                        />
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={goToMenu}
                        sx={{
                            mt: 2.2,
                            minHeight: 52,
                            borderRadius: "16px",
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: 16,
                            backgroundColor: "#003049",
                            "&:hover": {
                                backgroundColor: "#00263a",
                            },
                        }}
                    >
                        Order more
                    </Button>

                    {getTrackedOrders(outletId).length > 1 && (
                        <Button
                            fullWidth
                            variant="text"
                            onClick={() =>
                                navigate(`/${outletId}/orders`, { replace: true })
                            }
                            sx={{
                                mt: 0.6,
                                minHeight: 44,
                                textTransform: "none",
                                fontWeight: 800,
                                color: "#306781",
                            }}
                        >
                            View all orders
                        </Button>
                    )}

                    {isCompletedStatus(statusKey) && (
                        <Button
                            fullWidth
                            variant="text"
                            onClick={handleDismissOrder}
                            sx={{
                                mt: 0.2,
                                minHeight: 44,
                                textTransform: "none",
                                fontWeight: 800,
                                color: "#8BA3AE",
                            }}
                        >
                            Got it
                        </Button>
                    )}
                </motion.div>
            </Box>
        </Box>
    );
};

const InfoChip = ({ icon, label }) => (
    <Box
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 1,
            py: 0.55,
            borderRadius: "999px",
            backgroundColor: "#F3F8FB",
            color: "#306781",
            fontSize: 12,
            fontWeight: 700,
        }}
    >
        {icon}
        {label}
    </Box>
);

const BillRow = ({ label, value, emphasize = false }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 0.7,
        }}
    >
        <Typography
            sx={{
                color: emphasize ? "#0B4A67" : "#668594",
                fontSize: emphasize ? 15 : 13,
                fontWeight: emphasize ? 800 : 600,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                color: "#0B4A67",
                fontSize: emphasize ? 15 : 13,
                fontWeight: 800,
            }}
        >
            {value}
        </Typography>
    </Box>
);

export default OrderStatus;
